import { Popper, TextField } from '@mui/material';
import styled from 'styled-components';
import { Autocomplete, baseColors, shadows, ToggleButtonGroup, } from '@groupby/ui-components';
import { Button } from '@/components/button';
import { NoDataFallback } from '../no-data-fallback';
import { RuleFormTab } from '@/search-for-retail/rule/form/rule-form.constants';
export const StyledToggleButtonGroup = styled(ToggleButtonGroup) `
  /* Mobile-first */
  height: ${({ theme }) => theme.spacing(3.5)};
  
  &.MuiToggleButtonGroup-root > .toggle-button {
    font-size: 0.625rem;
    background-color: ${baseColors.background000};
    padding: ${({ theme }) => theme.spacing(0.5, 1)};
    
    /* Desktop and up */
    @media (min-width: 900px) {
      font-size: 0.75rem;
      padding: ${({ theme }) => theme.spacing(0.75, 1.5)};
    }
  }
  
  /* Desktop and up */
  @media (min-width: 900px) {
    height: ${({ theme }) => theme.spacing(4)};
  }
`;
export const StyledDrawerWrapper = styled.div `
  position: relative;
  /* Mobile-first: Full width, stacked below form */
  display: ${({ open }) => (open ? 'block' : 'none')};
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing(2)};
  
  /* Desktop and up: Show side-by-side, no top margin */
  @media (min-width: 900px) {
    width: ${({ activeTab }) => (activeTab === RuleFormTab.Facets ? '70%' : '50%')};
    margin-top: 0;
  }
`;
export const StyledDrawer = styled.div `
  position: relative;
  width: 100%;
  background-color: ${baseColors.white};

  /* Mobile-first: Auto height, scrollable content */
  height: auto;
  max-height: ${({ theme }) => theme.spacing(75)};
  
  /* Desktop and up: Fill remaining viewport height */
  @media (min-width: 900px) {
    ${({ $offsetY, theme }) => $offsetY && `
      height: calc(100vh - ${theme.spacing(4)} - ${$offsetY}px);
    `};
    max-height: none;
  }

  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.spacing(1)};
  border: ${({ theme }) => `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`};
  box-shadow: ${shadows.backgroundCard};
`;
export const StyledPanelRow = styled.div `
  /* Mobile-first */
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(0.75)};
  flex-wrap: wrap;
  width: ${({ $openModal }) => ($openModal ? '100%' : 'auto')};
  background-color: ${baseColors.white};

  ${({ theme, $openModal, $top }) => {
    if ($openModal) {
        return `padding: ${theme.spacing(0.75)} 0rem;`;
    }
    if ($top) {
        return `padding: ${theme.spacing(2, 1, 0.375, 1)};`;
    }
    return `padding: ${theme.spacing(0.75, 1)};`;
}}
  
  /* Desktop and up */
  @media (min-width: 900px) {
    gap: ${({ theme }) => theme.spacing(1)};
    width: ${({ $openModal, theme }) => ($openModal ? theme.spacing(78.75) : 'auto')};
    
    ${({ theme, $openModal, $top }) => {
    if ($openModal) {
        return `padding: ${theme.spacing(1)} 0rem;`;
    }
    if ($top) {
        return `padding: ${theme.spacing(3, 2, 0.5, 2)};`;
    }
    return `padding: ${theme.spacing(1, 2)};`;
}}
  }

  span {
    cursor: default;
    font-size: 0.875rem;
    
    @media (min-width: 900px) {
      font-size: 1rem;
    }
  }

  ${({ $sticky }) => typeof $sticky === 'number' && `
    position: sticky;
    top: ${$sticky}rem;
    z-index: ${$sticky === 0 ? 3 : 1};
  `}

  ${({ $lastSticky }) => $lastSticky && `
    display: unset;
    font-size: 0.6875rem;
    color: ${baseColors.primaryBlueMed600};
    z-index: 1;

    span {
      font-weight: 700;
    }
    
    @media (min-width: 900px) {
      font-size: 0.75rem;
    }
  `}

  &.preview-panel--config-row {
    /* Stack vertically on mobile and tablets */
    flex-direction: column;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing(1)};
    
    /* Horizontal layout on desktop (aligns with three-breakpoint system) */
    @media (min-width: 900px) {
      flex-direction: row;
    }
    
    .MuiInputBase-root {
      flex: 1;
      font-size: 0.875rem;
      width: 100%;
      
      @media (min-width: 900px) {
        font-size: 1rem;
        width: auto;
      }
    }
    
    /* Make buttons full width until desktop */
    button {
      width: 100%;
      
      @media (min-width: 900px) {
        width: auto;
      }
    }
  }
`;
export const StyledStickyHeader = styled.div `
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: ${baseColors.white};
`;
export const StyledStickyHeaderFullWidthContainer = styled.div `
  && > * {
    margin: ${({ theme }) => theme.spacing(2)};
  }
`;
export const StyledHideButton = styled(Button) `
  &.MuiButton-root {
    color: ${({ theme }) => theme.palette.error.dark};
    font-size: 0.875rem;
  }

  &.MuiButton-text {
    &:hover {
      color: ${({ theme }) => theme.palette.error.dark};
      background-color: ${({ theme }) => theme.palette.error.light};
    }
  }
`;
export const StyledPreviewText = styled.div `
  /* Mobile-first */
  cursor: default;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  font-weight: 500;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.accent.main};
  padding-left: ${({ theme }) => theme.spacing(0.5)};
  
  /* Tablet and up */
  @media (min-width: 600px) {
    font-size: 1rem;
    padding-left: ${({ theme }) => theme.spacing(0.75)};
  }
  
  /* Desktop and up */
  @media (min-width: 900px) {
    font-size: 1.125rem;
    padding-left: ${({ theme }) => theme.spacing(1)};
  }
`;
export const StyledFullScreenPreviewButtonWrapper = styled.div `
  display: flex;
  align-items: center;
`;
export const StyledSearchQueryInput = styled(TextField) `
  && {
    /* Mobile-first */
    flex: 1;
    min-width: 0;
    
    /* Ensure minimum width to accommodate label at desktop breakpoint */
    @media (min-width: 900px) {
      min-width: ${({ theme }) => theme.spacing(25)};
    }

    .MuiInputBase-root {
      max-height: ${({ theme }) => theme.spacing(4)};
      font-size: 0.875rem;
      
      @media (min-width: 900px) {
        max-height: ${({ theme }) => theme.spacing(4.5)};
        font-size: 1rem;
      }
    }
    
    .MuiInputBase-input {
      padding: ${({ theme }) => theme.spacing(1, 1.5)};
      font-size: 0.875rem;
      
      @media (min-width: 900px) {
        padding: ${({ theme }) => theme.spacing(1.25, 2)};
        font-size: 1rem;
      }
    }
    
    /* Search icon sizing */
    .MuiInputAdornment-root {
      margin-right: ${({ theme }) => theme.spacing(0.5)};
      
      .search-icon {
        width: ${({ theme }) => theme.spacing(2.25)};
        height: ${({ theme }) => theme.spacing(2.25)};
        
        @media (min-width: 900px) {
          width: ${({ theme }) => theme.spacing(2.5)};
          height: ${({ theme }) => theme.spacing(2.5)};
        }
      }
    }

    /* End adornment (clear button) positioning */
    .MuiInputAdornment-positionEnd {
      margin-right: 0;
      margin-left: ${({ theme }) => theme.spacing(0.5)};
      
      .MuiIconButton-root {
        padding: ${({ theme }) => theme.spacing(0.5)};
      }
    }

    fieldset {
      inset: 0;
      height: ${({ theme }) => theme.spacing(4)};
      
      @media (min-width: 900px) {
        height: ${({ theme }) => theme.spacing(4.5)};
      }
    }

    fieldset > legend {
      display: none;
    }

    label {
      /* Position label after the search icon
       * Calculation: icon width + margin-right + input padding
       * Mobile: 18px (2.25 spacing) + 4px (0.5 spacing) + 12px (1.5 spacing) = 34px
       * Desktop: 20px (2.5 spacing) + 4px (0.5 spacing) + 16px (2 spacing) = 40px
       */
      transform: translate(calc(18px + 4px + 12px), 7px) scale(1);
      background-color: white;
      z-index: 2;
      padding-left: ${({ theme }) => theme.spacing(0.625)};
      padding-right: ${({ theme }) => theme.spacing(0.625)};
      font-size: 0.875rem;

      @media (min-width: 900px) {
        transform: translate(calc(20px + 4px + 16px), 8px) scale(1);
        font-size: 1rem;
      }
    }

    label.Mui-focused {
      transform: translate(${({ theme }) => theme.spacing(1.75)}, ${({ theme }) => theme.spacing(-1.125)}) scale(0.75);
      
      @media (min-width: 900px) {
        transform: translate(${({ theme }) => theme.spacing(1.75)}, ${({ theme }) => theme.spacing(-1.25)}) scale(0.75);
      }
    }
    
    label.MuiFormLabel-filled {
      transform: translate(${({ theme }) => theme.spacing(1.75)}, ${({ theme }) => theme.spacing(-1.125)}) scale(0.75);
      
      @media (min-width: 900px) {
        transform: translate(${({ theme }) => theme.spacing(1.75)}, ${({ theme }) => theme.spacing(-1.25)}) scale(0.75);
      }
    }
  }
`;
export const StyledSearchBrowseInput = styled(Autocomplete) `
  flex: 1 1 0;

  fieldset {
    inset: 0;
  }

  ${({ disabled }) => disabled && `
    pointer-events: none;
  `}

  &&&& fieldset:first-of-type {
    border-color: ${({ theme }) => theme.palette.grey[200]};
    &:hover {
      // temporarily matched the colour of the MUI outlined input
      // both will be changed when adhering to the new design guidelines
      border-color: #000000de;
    }
  }

  &&& .MuiInputBase-root {
    min-height: 0;
    padding: ${({ theme }) => `0 0 0 ${theme.spacing(1)}`};
  }

  && {
    fieldset > legend {
      display: none;
    }

    .MuiFormControl-root > .MuiInputLabel-shrink {
      transform: translate(${({ theme }) => theme.spacing(3.75)}, ${({ theme }) => theme.spacing(0.875)}) scale(1);
      background-color: ${({ background }) => background};
      z-index: 2;
      padding-left: ${({ theme }) => theme.spacing(0.625)};
      padding-right: ${({ theme }) => theme.spacing(0.625)};
    }

    .MuiFormControl-root > .MuiInputLabel-shrink.Mui-focused {
      transform: translate(14px, -10px) scale(0.75);

    }.MuiFormControl-root > .MuiInputLabel-shrink.MuiFormLabel-filled {
      transform: translate(14px, -10px) scale(0.75);
    }
  }
`;
export const StyledPanelText = styled.div `
  /* Mobile-first */
  cursor: default;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${({ theme }) => theme.palette.grey[600]};
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(2)}`};

  &.preview-panel--rule-trigger {
    color: ${({ theme }) => theme.palette.accent.main};
  }
  
  /* Desktop and up */
  @media (min-width: 900px) {
    font-size: 1rem;
    line-height: 1.625rem;
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  }
`;
export const StyledPreviewResultsContainer = styled.div `
  min-height: 0;
  display: flex;
  justify-content: space-between;
  height: 100%;
  flex-direction: column;
`;
export const StyledPreviewResults = styled.div `
  /* Mobile-first */
  display: flex;
  height: 100%;
  overflow: hidden;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(0.75)};
  flex-direction: column;
  
  /* Desktop and up */
  @media (min-width: 900px) {
    gap: ${({ theme }) => theme.spacing(2)};
    margin-bottom: ${({ theme }) => theme.spacing(1)};
    flex-direction: row;
  }
`;
export const StyledResultsContent = styled.div `
  /* Mobile-first */
  overflow: hidden;
  display: flex;
  flex: 1;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(0, 1, 1, 1)};
  justify-content: space-between;
  flex-direction: column;
  
  /* Desktop and up */
  @media (min-width: 900px) {
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => theme.spacing(0, 2, 2, 2)};
    flex-direction: row;
  }
`;
export const StyledPerformSearchButton = styled(Button) `
  /* Mobile-first */
  font-size: 0.6875rem;
  height: ${({ theme }) => theme.spacing(4)};
  white-space: nowrap;
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  min-width: ${({ theme }) => theme.spacing(5.5)};
  
  /* Desktop and up */
  @media (min-width: 900px) {
    font-size: 0.75rem;
    height: ${({ theme }) => theme.spacing(4.5)};
    padding: ${({ theme }) => theme.spacing(1, 2)};
    min-width: auto;
  }
`;
export const StyledOriginalRuleNotification = styled.ul `
  cursor: default;
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 3)};
  padding: 0;
  white-space: nowrap;
`;
export const StyledRuleTriggerNotification = styled.div `
  border: ${({ theme }) => theme.spacing(0.125)} ${baseColors.primaryBlueAccent100} solid;
  border-radius: ${({ theme }) => theme.spacing(1)};
  height: ${({ theme }) => theme.spacing(7)};
  display: flex;
  flex-basis: 100%;
  align-items: center;
  background-color: ${baseColors.primaryBlueAccent050};

  .MuiSvgIcon-root {
    fill: ${baseColors.primaryBlueAccent500};
    padding: ${({ theme }) => theme.spacing(1.5)};
  }
`;
export const StyledAppliedBias = styled.span `
  font-weight: 700
`;
export const StyledAppliedRule = styled.span `
  color: ${baseColors.primaryGreen300};
  text-decoration-line: underline;
  font-weight: 700;

  cursor: pointer;
  .MuiTypography-root {
    cursor: pointer;
  }
`;
export const StyledNoDataFallback = styled(NoDataFallback) `
  /* Mobile-first */
  margin: ${({ theme }) => theme.spacing(4, 2, 2, 2)};
  font-size: 0.875rem;
  
  b {
    margin-left: ${({ theme }) => theme.spacing(0.625)};
  }
  
  /* Desktop and up */
  @media (min-width: 900px) {
    margin: ${({ theme }) => theme.spacing(8, 4, 4, 4)};
    font-size: 1rem;
  }
`;
export const StyledViewCustomizeViewButton = styled(Button) `
  margin-left: ${({ theme }) => theme.spacing(1.25)};
  height: ${({ theme }) => theme.spacing(4.5)};

  && {
    .view-customize-icon {
      cursor: pointer;
      transform: rotate(180deg);
    }
  }
`;
export const StyledPopper = styled(Popper) `
  && {

    ${({ isTriggered }) => isTriggered && `
      .MuiAutocomplete-paper {
        padding: 0;
      }

      .MuiAutocomplete-noOptions {
        display: none;
      }
    `}


  }
`;
